import React, { useState } from "react";
import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";
import LabelWithInput from "@components/input/LabelWithInput";
import ToastAlert from "@components/alert/ToastAlert";
import { useApiRequest } from "@utils/ApiRequest";

const ProductClassificationFields = () => {
  const { getAudienceCategoryList, getTagList } = useApiRequest();
  const { watch, setValue } = useFormContext();
  const audienceIds: number[] = watch("audience_category_ids") || [];
  const patterns: any[] = watch("patterns") || [];
  const [nameKo, setNameKo] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [nameJa, setNameJa] = useState("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const { data: audiences = [] } = useQuery({
    queryKey: ["audienceCategoryList"],
    queryFn: getAudienceCategoryList,
  });
  const { data: patternTags = [] } = useQuery({
    queryKey: ["tagList", "pattern"],
    queryFn: () => getTagList("pattern"),
  });

  const toggleAudience = (id: number) => {
    const next = audienceIds.includes(id)
      ? audienceIds.filter((value) => value !== id)
      : [...audienceIds, id];
    setValue("audience_category_ids", next, { shouldValidate: true });
  };

  const togglePattern = (tag: any) => {
    const selected = patterns.some((pattern) => Number(pattern.tag_id) === Number(tag.id));
    const next = selected
      ? patterns.filter((pattern) => Number(pattern.tag_id) !== Number(tag.id))
      : [...patterns, { tag_id: Number(tag.id) }];
    setValue("patterns", next, { shouldValidate: true });
  };

  const addCustomPattern = () => {
    const ko = nameKo.trim();
    const en = nameEn.trim();
    const ja = nameJa.trim();
    if (!ko || !en || !ja) {
      ToastAlert("패턴명은 한글·영문·일문 모두 입력해주세요.");
      return;
    }
    const key = `${ko.toLocaleLowerCase()}:${en.toLocaleLowerCase()}`;
    const duplicated = patterns.some(
      (pattern, index) =>
        index !== editingIndex && !pattern.tag_id &&
        `${(pattern.name_ko || "").trim().toLocaleLowerCase()}:${(pattern.name_en || "").trim().toLocaleLowerCase()}` === key,
    );
    if (duplicated) {
      ToastAlert("이미 추가된 패턴입니다.");
      return;
    }
    setValue("patterns", [...patterns.filter((_, index) => index !== editingIndex), { tag_id: null, name_ko: ko, name_en: en, name_ja: ja }], {
      shouldValidate: true,
    });
    setNameKo("");
    setNameEn("");
    setNameJa("");
    setEditingIndex(null);
  };

  const removeCustomPattern = (index: number) => {
    setValue("patterns", patterns.filter((_, current) => current !== index), { shouldValidate: true });
  };

  return (
    <Wrapper>
      <LabelWithInput label="앱 대분류" required>
        <HelpText>여성·남성·공용·아동·잡화 중 여러 항목을 선택할 수 있습니다.</HelpText>
        <ChipGroup>
          {audiences.map((audience: any) => (
            <ChipButton
              key={audience.id}
              type="button"
              active={audienceIds.includes(Number(audience.id))}
              onClick={() => toggleAudience(Number(audience.id))}>
              {audience.name_ko} / {audience.name_en}
            </ChipButton>
          ))}
        </ChipGroup>
      </LabelWithInput>

      <LabelWithInput label="패턴" required>
        <HelpText>프리셋을 복수 선택하거나 직접 한글·영문·일문명을 추가할 수 있습니다.</HelpText>
        <ChipGroup>
          {patternTags.filter((tag: any) => tag.is_active).map((tag: any) => (
            <ChipButton
              key={tag.id}
              type="button"
              active={patterns.some((pattern) => Number(pattern.tag_id) === Number(tag.id))}
              onClick={() => togglePattern(tag)}>
              {tag.name_ko} / {tag.name_en}
            </ChipButton>
          ))}
        </ChipGroup>
        <CustomRow>
          <TextInput value={nameKo} onChange={(event) => setNameKo(event.target.value)} placeholder="직접 패턴 (한글) *" />
          <TextInput value={nameEn} onChange={(event) => setNameEn(event.target.value)} placeholder="Custom pattern (English) *" />
          <TextInput value={nameJa} onChange={(event) => setNameJa(event.target.value)} placeholder="パターン名 (日本語) *" />
          <AddButton type="button" onClick={addCustomPattern}>추가</AddButton>
        </CustomRow>
        {patterns.map((pattern, index) =>
          pattern.tag_id ? null : (
            <CustomPattern key={`${pattern.name_ko}-${pattern.name_en}-${index}`}>
              <span>{[pattern.name_ko, pattern.name_en, pattern.name_ja].filter(Boolean).join(" / ")}</span>
              <button type="button" onClick={() => { setEditingIndex(index); setNameKo(pattern.name_ko || ""); setNameEn(pattern.name_en || ""); setNameJa(pattern.name_ja || ""); }}>수정</button>
              <button type="button" onClick={() => removeCustomPattern(index)}>삭제</button>
            </CustomPattern>
          ),
        )}
      </LabelWithInput>
    </Wrapper>
  );
};

export default ProductClassificationFields;

const Wrapper = styled.div({ display: "flex", flexDirection: "column", gap: "2rem" });
const HelpText = styled.p(({ theme }) => ({ margin: "0 0 1rem", color: theme.color.light_gray, fontSize: "1.2rem" }));
const ChipGroup = styled.div({ display: "flex", flexWrap: "wrap", gap: "0.8rem" });
const ChipButton = styled.button<{ active: boolean }>(({ theme, active }) => ({
  padding: "0.9rem 1.2rem",
  border: `1px solid ${active ? theme.color.black : theme.color.border_gray}`,
  backgroundColor: active ? theme.color.black : theme.color.white,
  color: active ? theme.color.white : theme.color.black,
  borderRadius: "999px",
  fontSize: "1.2rem",
}));
const CustomRow = styled.div({ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.8rem", marginTop: "1rem" });
const TextInput = styled.input(({ theme }) => ({ minWidth: 0, padding: "1rem", border: `1px solid ${theme.color.border_gray}` }));
const AddButton = styled.button(({ theme }) => ({ padding: "0 1.4rem", border: "none", background: theme.color.black, color: theme.color.white }));
const CustomPattern = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  marginTop: "0.8rem",
  padding: "0.8rem 1rem",
  background: theme.color.border_gray,
  button: { border: "none", background: "transparent", textDecoration: "underline" },
}));
